import React from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { TrendingUp, Wallet } from "lucide-react";

/**
 * Bulletins history chart: 12 months of brut + estimated net.
 *
 * @param {Array} data  [{ label, mois, annee, brut_total, net_estime, count }]
 * @param {string} title - displayed in the header
 * @param {string} subtitle - optional muted subtitle
 */
export default function BulletinsHistoryChart({ data = [], title = "Historique 12 mois", subtitle = "Cumul brut & net estimé par mois", testid = "bulletins-history" }) {
  const totalBrut = data.reduce((s, d) => s + (d.brut_total || 0), 0);
  const totalNet = data.reduce((s, d) => s + (d.net_estime || 0), 0);
  const monthsWithData = data.filter((d) => (d.brut_total || 0) > 0).length;
  const moyenneBrut = monthsWithData > 0 ? totalBrut / monthsWithData : 0;

  return (
    <div className="bg-white border border-[#DEE2E6]" data-testid={testid}>
      <div className="p-4 border-b border-[#DEE2E6] flex items-center gap-2 flex-wrap">
        <TrendingUp className="h-4 w-4 text-[#002FA7]" />
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-[0.25em] font-semibold">{title}</div>
          {subtitle && <div className="text-[10px] text-[#868E96] mt-0.5">{subtitle}</div>}
        </div>
        <div className="flex gap-2 items-center text-xs">
          <span className="flex items-center gap-1.5">
            <span className="h-3 w-3 bg-[#002FA7]" /> <span className="text-[#495057] font-semibold">Brut</span>
          </span>
          <span className="flex items-center gap-1.5 ml-2">
            <span className="h-3 w-3 bg-[#FFD700]" /> <span className="text-[#495057] font-semibold">Net estimé</span>
          </span>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-3 border-b border-[#DEE2E6] divide-x divide-[#DEE2E6]" data-testid={`${testid}-kpis`}>
        <Kpi label="Total brut 12 mois" value={totalBrut.toFixed(2)} unit="€" />
        <Kpi label="Net estimé 12 mois" value={totalNet.toFixed(2)} unit="€" highlight />
        <Kpi label="Moyenne brut / mois" value={moyenneBrut.toFixed(2)} unit="€" muted hint={`${monthsWithData} mois actifs`} />
      </div>

      {/* Chart */}
      <div className="p-4 h-72">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }} barCategoryGap="20%">
            <defs>
              <linearGradient id="brutBar" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#002FA7" stopOpacity={1} />
                <stop offset="100%" stopColor="#002FA7" stopOpacity={0.7} />
              </linearGradient>
              <linearGradient id="netBar" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#FFD700" stopOpacity={1} />
                <stop offset="100%" stopColor="#FFD700" stopOpacity={0.65} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="0" stroke="#DEE2E6" vertical={false} />
            <XAxis dataKey="label" stroke="#868E96" fontSize={11} tickLine={false} axisLine={{ stroke: "#DEE2E6" }} />
            <YAxis stroke="#868E96" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${v}€`} />
            <Tooltip
              cursor={{ fill: "rgba(0,47,167,0.08)" }}
              contentStyle={{ background: "#0A0A0A", border: "none", borderRadius: 0, color: "white", fontSize: 12, padding: "8px 12px" }}
              labelStyle={{ color: "#FFD700", textTransform: "uppercase", letterSpacing: "0.15em", fontSize: 10, fontWeight: 600 }}
              formatter={(v, name) => [`${Number(v).toFixed(2)} €`, name === "brut_total" ? "Brut" : "Net estimé"]}
            />
            <Bar dataKey="brut_total" name="brut_total" fill="url(#brutBar)" radius={[2, 2, 0, 0]} />
            <Bar dataKey="net_estime" name="net_estime" fill="url(#netBar)" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Empty state hint */}
      {monthsWithData === 0 && (
        <div className="p-3 border-t border-[#DEE2E6] text-xs text-[#868E96] flex items-center gap-2 bg-[#F8F9FA]">
          <Wallet className="h-3.5 w-3.5" />
          Aucun bulletin enregistré sur les 12 derniers mois. L'historique se remplira automatiquement.
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, unit, highlight, muted, hint }) {
  return (
    <div className={`p-4 ${highlight ? "bg-[#FFFBEB]" : muted ? "bg-[#F8F9FA]" : "bg-white"}`}>
      <div className={`text-[10px] uppercase tracking-[0.2em] font-semibold ${highlight ? "text-[#92400E]" : "text-[#868E96]"}`}>{label}</div>
      <div className="font-display text-2xl lg:text-3xl font-black tracking-tighter mt-1">
        {value}<span className="text-sm ml-1 text-[#868E96] font-normal">{unit}</span>
      </div>
      {hint && <div className="text-[10px] text-[#868E96] mt-1">{hint}</div>}
    </div>
  );
}
